import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth, useProfile, useQualifications, useWorkExperience, useReferences, useDocuments } from '../../hooks';
import { Button, Card, CardHeader, CardBody, Avatar, Alert, Spinner } from '../../components/ui';
import { PassportStatusCard } from '../../components/PassportStatus';
import { VerificationBadge } from '../../components';
import {
  getFieldOfWorkLabel,
  formatDate,
} from '../../utils';
import { ShareProfileModal } from '../share';
import { PublicPassportCard } from './PublicPassportCard';
import type { DocumentType, Profile as ProfileType } from '../../types';

const OFFICIAL_DOC_SLOTS: { type: DocumentType; label: string; icon: string }[] = [
  {
    type: 'id_document',
    label: 'Identity Document',
    icon: 'M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0',
  },
  {
    type: 'police_clearance',
    label: 'Police Clearance',
    icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  },
];

function getMissingProfileFields(
  profile: ProfileType | null,
  qualCount: number,
  expCount: number,
  hasIdDoc: boolean,
  hasPoliceClearance: boolean
): string[] {
  const missing: string[] = [];
  if (!profile?.first_name) missing.push('First name');
  if (!profile?.last_name) missing.push('Surname');
  if (!profile?.phone) missing.push('Phone');
  if (!profile?.contact_email) missing.push('Contact email');
  if (!profile?.field_of_work) missing.push('Field of work');
  if (!profile?.bio) missing.push('Bio');
  if (qualCount === 0) missing.push('At least one qualification');
  if (expCount === 0) missing.push('At least one work experience entry');
  if (!hasIdDoc) missing.push('Identity document');
  if (!hasPoliceClearance) missing.push('Police clearance');
  return missing;
}

export const Profile: React.FC = () => {
  const { user } = useAuth();
  const { profile, loading: profileLoading, error, clearError } = useProfile();
  const { qualifications, loading: qualificationsLoading } = useQualifications();
  const { workExperience, loading: experienceLoading } = useWorkExperience();
  const { references } = useReferences();
  const { documents } = useDocuments();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showShareModal, setShowShareModal] = useState(false);

  useEffect(() => {
    if (searchParams.get('share') !== '1') return;
    setShowShareModal(true);
    const next = new URLSearchParams(searchParams);
    next.delete('share');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const loading = profileLoading || qualificationsLoading || experienceLoading;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <Alert variant="danger" onClose={clearError}>{error}</Alert>
      </div>
    );
  }

  const officialDocs = documents.filter(
    d => d.document_type === 'id_document' || d.document_type === 'police_clearance',
  );
  const hasIdDoc = officialDocs.some(d => d.document_type === 'id_document');
  const hasPoliceClearance = officialDocs.some(d => d.document_type === 'police_clearance');
  const missing = getMissingProfileFields(profile, qualifications.length, workExperience.length, hasIdDoc, hasPoliceClearance);
  const fullName = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ');

  return (
    <div className="space-y-5">

      {/* Passport Status */}
      <PassportStatusCard
        profile={profile}
        qualifications={qualifications}
        verifications={[]}
        workExperience={workExperience}
        documents={documents}
      />

      {/* Completion checklist */}
      {missing.length > 0 && (
        <Card className="!bg-brand-50 !border-brand-200 !shadow-none">
          <CardHeader className="!border-brand-200">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <p className="text-sm font-semibold text-brand-900">Complete your passport</p>
            </div>
          </CardHeader>
          <CardBody>
            <div className="flex flex-wrap gap-2">
              {missing.map(field => (
                <span key={field} className="inline-flex items-center gap-1.5 text-xs bg-white border border-brand-200 text-brand-700 px-2.5 py-1 rounded-full font-medium">
                  <span className="w-3.5 h-3.5 rounded-full border-2 border-brand-400 flex-shrink-0" />
                  {field}
                </span>
              ))}
            </div>
            {missing.some(f => !f.includes('qualification') && !f.includes('work experience')) && (
              <Link to="/profile/edit" className="inline-block mt-3">
                <Button variant="primary" size="sm">Edit Profile</Button>
              </Link>
            )}
          </CardBody>
        </Card>
      )}

      {/* Profile Card */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              <h1 className="text-lg font-bold text-gray-900">My Passport</h1>
            </div>
            <div className="grid grid-cols-2 gap-2 w-full sm:flex sm:flex-wrap sm:w-auto">
              <Link to="/profile/cv" className="min-w-0">
                <Button variant="secondary" size="sm" fullWidth>
                  <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Export CV
                </Button>
              </Link>
              <Button variant="secondary" size="sm" fullWidth className="sm:w-auto" onClick={() => setShowShareModal(true)}>
                <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
                Share
              </Button>
              <Link to="/profile/shares" className="min-w-0">
                <Button variant="secondary" size="sm" fullWidth>
                  Shared links
                </Button>
              </Link>
              <Link to="/profile/edit" className="min-w-0">
                <Button variant="primary" size="sm" fullWidth>
                  <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit Profile
                </Button>
              </Link>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {profile ? (
            <div>
              {/* Photo + Name row */}
              <div className="flex flex-col sm:flex-row items-start gap-5 pb-5 border-b border-gray-100">
                <Avatar
                  src={profile.photo_url}
                  alt={fullName || 'Profile'}
                  size="xl"
                  fallback={profile.first_name?.charAt(0) || profile.last_name?.charAt(0)}
                  className="ring-4 ring-brand-50 flex-shrink-0"
                />
                <div className="min-w-0 w-full text-left">
                  <h2 className="text-xl font-bold text-gray-900">{fullName || 'Name not set'}</h2>
                  <p className="text-sm text-brand-600 font-medium mt-0.5">
                    {profile.field_of_work ? getFieldOfWorkLabel(profile.field_of_work) : 'Field not specified'}
                  </p>
                  {profile.bio && (
                    <p className="text-sm text-gray-500 mt-2 leading-relaxed">{profile.bio}</p>
                  )}
                </div>
              </div>

              {/* Contact & details */}
              <div className="pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InfoField
                  icon="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  label="Email"
                  value={user?.email}
                />
                {profile.phone && (
                  <InfoField
                    icon="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    label="Phone"
                    value={profile.phone}
                  />
                )}
                {profile.contact_email && profile.contact_email !== user?.email && (
                  <InfoField
                    icon="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    label="Contact Email"
                    value={profile.contact_email}
                  />
                )}
                {profile.date_of_birth && (
                  <InfoField
                    icon="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    label="Date of Birth"
                    value={new Date(profile.date_of_birth).toLocaleDateString()}
                  />
                )}
                {profile.rsa_id_number && (
                  <InfoField
                    icon="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2"
                    label="RSA ID Number"
                    value={profile.rsa_id_number}
                  />
                )}
                {profile.passport_no && (
                  <InfoField
                    icon="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    label="Passport Number"
                    value={profile.passport_no}
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-10 px-4">
              <div className="mx-auto w-16 h-16 bg-brand-50 rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-brand-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-1">Set up your profile</h3>
              <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto leading-relaxed">
                Your profile is your professional passport. Add your details so employers can find and verify your credentials.
              </p>
              <Link to="/profile/edit">
                <Button variant="primary">Create Profile</Button>
              </Link>
            </div>
          )}
        </CardBody>
      </Card>

      <PublicPassportCard profile={profile} />

      {/* Qualifications */}
      <Card collapsible>
        <CardHeader>
          <div className="flex items-start gap-2 min-w-0">
            <svg className="w-4 h-4 text-emerald-500 shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <h2 className="text-base font-bold text-gray-900 leading-snug">Qualifications</h2>
                {qualifications.length > 0 && (
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">{qualifications.length}</span>
                )}
              </div>
              <Link to="/qualifications" className="inline-block mt-1 text-xs font-semibold text-brand-600 hover:text-brand-700">
                View all
              </Link>
            </div>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {qualifications.length > 0 ? (
            <div>
              {qualifications.slice(0, 3).map((qual, i) => {
                return (
                <div key={qual.id} className={`px-5 py-4 ${i < Math.min(qualifications.length, 3) - 1 ? 'border-b border-gray-50' : ''}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 bg-brand-50">
                      <svg className="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-sm text-gray-900 break-words">{qual.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{qual.institution}</p>
                      {qual.issue_date && (
                        <p className="text-xs text-gray-400 mt-0.5">Issued: {formatDate(qual.issue_date)}</p>
                      )}
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        <VerificationBadge status={qual.verification_status} />
                        {qual.expiry_date && new Date(qual.expiry_date) < new Date() && (
                          <span className="text-xs bg-red-50 text-red-600 px-1.5 py-0.5 rounded font-medium">Expired</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                );
              })}
              {qualifications.length > 3 && (
                <div className="px-5 py-3 border-t border-gray-50 bg-gray-50/50">
                  <Link to="/qualifications" className="text-xs text-brand-600 font-semibold hover:text-brand-700">
                    View {qualifications.length - 3} more →
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <div className="px-5 py-8 text-center">
              <div className="w-10 h-10 bg-gray-50 rounded-xl flex items-center justify-center mx-auto mb-3">
                <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-sm text-gray-400 mb-3">No qualifications added yet</p>
              <Link to="/qualifications/add">
                <Button variant="primary" size="sm">Add Qualification</Button>
              </Link>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Work Experience */}
      <Card collapsible>
        <CardHeader>
          <div className="flex items-start gap-2 min-w-0">
            <svg className="w-4 h-4 text-blue-500 shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <h2 className="text-base font-bold text-gray-900 leading-snug">Work Experience</h2>
                {workExperience.length > 0 && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-semibold">{workExperience.length}</span>
                )}
              </div>
              <Link to="/experience" className="inline-block mt-1 text-xs font-semibold text-brand-600 hover:text-brand-700">
                View all
              </Link>
            </div>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {workExperience.length > 0 ? (
            <div>
              {workExperience.slice(0, 3).map((exp, i) => (
                <div key={exp.id} className={`px-5 py-4 ${i < Math.min(workExperience.length, 3) - 1 ? 'border-b border-gray-50' : ''}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 002 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-gray-900 break-words">{exp.job_title}</p>
                      <p className="text-xs text-gray-500 mt-0.5 break-words">{exp.employer}{exp.location ? ` · ${exp.location}` : ''}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {formatDate(exp.start_date)} – {exp.is_current ? 'Present' : exp.end_date ? formatDate(exp.end_date) : ''}
                        {exp.is_current && <span className="ml-1.5 text-xs bg-brand-50 text-brand-600 px-1.5 py-0.5 rounded font-medium">Current</span>}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              {workExperience.length > 3 && (
                <div className="px-5 py-3 border-t border-gray-50 bg-gray-50/50">
                  <Link to="/experience" className="text-xs text-brand-600 font-semibold hover:text-brand-700">
                    View {workExperience.length - 3} more →
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <div className="px-5 py-8 text-center">
              <div className="w-10 h-10 bg-gray-50 rounded-xl flex items-center justify-center mx-auto mb-3">
                <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-sm text-gray-400 mb-3">No work experience added yet</p>
              <Link to="/experience/add">
                <Button variant="primary" size="sm">Add Experience</Button>
              </Link>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Official Documents */}
      <Card collapsible>
        <CardHeader>
          <div className="flex items-start gap-2 min-w-0">
            <svg className="w-4 h-4 text-amber-500 shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <h2 className="text-base font-bold text-gray-900 leading-snug">Official Documents</h2>
                {officialDocs.length > 0 && (
                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">{officialDocs.length}</span>
                )}
              </div>
              <Link to="/official-documents" className="inline-block mt-1 text-xs font-semibold text-brand-600 hover:text-brand-700">
                View all
              </Link>
            </div>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {officialDocs.length > 0 ? (
            <div>
              {OFFICIAL_DOC_SLOTS.map((slot, i) => {
                const doc = documents.find(d => d.document_type === slot.type);
                return (
                  <div key={slot.type} className={`px-5 py-4 ${i < OFFICIAL_DOC_SLOTS.length - 1 ? 'border-b border-gray-50' : ''}`}>
                    <div className="flex items-start gap-3 min-w-0">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${doc ? 'bg-amber-50' : 'bg-gray-50'}`}>
                        <svg className={`w-4 h-4 ${doc ? 'text-amber-600' : 'text-gray-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={slot.icon} />
                        </svg>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm text-gray-900 break-words">{slot.label}</p>
                        {doc ? (
                          <>
                            <p className="text-xs text-gray-500 mt-0.5 break-words">{doc.file_name}</p>
                            <p className="text-xs text-gray-400 mt-0.5">Uploaded: {formatDate(doc.created_at)}</p>
                            <div className="mt-1.5">
                              <VerificationBadge status={doc.verification_status} />
                            </div>
                          </>
                        ) : (
                          <>
                            <p className="text-xs text-gray-400 mt-0.5">Not uploaded yet</p>
                            <span className="mt-1.5 inline-block text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium">Required</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="px-5 py-8 text-center">
              <div className="w-10 h-10 bg-gray-50 rounded-xl flex items-center justify-center mx-auto mb-3">
                <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <p className="text-sm text-gray-400 mb-3">No official documents uploaded yet</p>
              <Link to="/official-documents">
                <Button variant="primary" size="sm">Add Official Documents</Button>
              </Link>
            </div>
          )}
        </CardBody>
      </Card>

      <ShareProfileModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        userId={user?.id ?? ''}
        profile={profile}
        workExperience={workExperience}
        qualifications={qualifications}
        references={references}
        documents={documents}
      />
    </div>
  );
};

const InfoField: React.FC<{ icon: string; label: string; value?: string | null }> = ({ icon, label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 bg-gray-50 rounded-lg px-3 py-2.5">
      <div className="w-7 h-7 rounded-md bg-white border border-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm">
        <svg className="w-3.5 h-3.5 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
        </svg>
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-400 font-medium uppercase tracking-wide leading-none mb-0.5">{label}</p>
        <p className="text-sm text-gray-800 font-medium break-words">{value}</p>
      </div>
    </div>
  );
};
